import { Injectable } from "@angular/core";
import { LocalConferenceStreamsListService } from "@youm-front/conference-stream/data-access";
import { RtpCodecCapability } from "mediasoup-client/lib/RtpParameters";
import {
  firstValueFrom,
  map,
  mergeMap,
  Observable,
  shareReplay,
  startWith
} from "rxjs";
import { LocalAnyConferenceStream } from "youm-lib/conference";

@Injectable()
export class RtpCapabilityService {
  static readonly WARNING_MESSAGE = $localize`Your browser cannot send or receive required audio/video \
format (Opus/H264), so others may not see or hear you correctly. Please copy \
the message below and contact the producer or support, or join from an \
up-to-date Chrome, Edge, Firefox or Safari.`;

  // Keep in sync with video/H264 codec on a media server.
  private static VIDEO_CLOCK_RATE = 90000;
  private static VIDEO_MIME_TYPE = "video/h264";
  private static VIDEO_CAPABILITIES = [
    "packetization-mode=1",
    // level-asymmetry-allowed=1, level_idc can be any
    "profile-level-id=42e0"
  ];

  // Keep in sync with audio/OPUS codec on a media server.
  private static AUDIO_CLOCK_RATE = 48000;
  private static AUDIO_MIME_TYPE = "audio/opus";
  private static AUDIO_CHANNELS = 2;

  readonly isRtpCompatible$: Observable<boolean>;
  private readonly isBrowserRtpCompatible: boolean = false;
  private readonly isDeviceRtpCompatible$: Observable<boolean>;

  constructor(
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService
  ) {
    const senderVideoCodecs =
      RTCRtpSender.getCapabilities("video")?.codecs || [];
    const senderAudioCodecs =
      RTCRtpSender.getCapabilities("audio")?.codecs || [];
    const receiverVideoCodecs =
      RTCRtpReceiver.getCapabilities("video")?.codecs || [];
    const receiverAudioCodecs =
      RTCRtpReceiver.getCapabilities("audio")?.codecs || [];

    this.isBrowserRtpCompatible =
      senderVideoCodecs.some((_) => this.isVideoCodecCompatible(_)) &&
      senderAudioCodecs.some((_) => this.isAudioCodecCompatible(_)) &&
      receiverVideoCodecs.some((_) => this.isVideoCodecCompatible(_)) &&
      receiverAudioCodecs.some((_) => this.isAudioCodecCompatible(_));

    this.isDeviceRtpCompatible$ =
      this.localConferenceStreamsListService.streams$.pipe(
        mergeMap((_) => (_[0] ? [_[0]] : [])),
        mergeMap((_) => this.isDeviceRtpCompatible(_)),
        shareReplay(1)
      );

    // Layered design: the static browser check (getCapabilities) is the EARLY signal -
    // it can run before any mediasoup object exists. The loaded-Device check below is
    // the AUTHORITATIVE verdict - it reflects what mediasoup-client actually negotiated
    // with this room's router. Until the device loads we optimistically report
    // compatible; the verdict corrects itself the moment the device is loaded.
    this.isRtpCompatible$ = this.isDeviceRtpCompatible$.pipe(
      startWith(true),
      map((_) => _ && this.isBrowserRtpCompatible),
      shareReplay(1)
    );
  }

  private async isDeviceRtpCompatible(stream: LocalAnyConferenceStream) {
    const sendRtpCapabilities = await firstValueFrom(
      stream.sendRtpCapabilities$
    );
    const sendRtpCapabilitiesCodecs = sendRtpCapabilities.codecs || [];

    const recvRtpCapabilities = await firstValueFrom(
      stream.recvRtpCapabilities$
    );
    const recvRtpCapabilitiesCodecs = recvRtpCapabilities.codecs || [];

    // every direction x kind must be compatible; the audio terms here are
    // send-audio and recv-audio (a previous revision checked recv-audio twice
    // and never checked send-audio)
    return (
      sendRtpCapabilitiesCodecs.some((_) =>
        this.isDeviceVideoCodecCompatible(_)
      ) &&
      sendRtpCapabilitiesCodecs.some((_) => this.isAudioCodecCompatible(_)) &&
      recvRtpCapabilitiesCodecs.some((_) =>
        this.isDeviceVideoCodecCompatible(_)
      ) &&
      recvRtpCapabilitiesCodecs.some((_) => this.isAudioCodecCompatible(_))
    );
  }

  private isDeviceVideoCodecCompatible(codec: RtpCodecCapability) {
    const sdpFmtpLine = codec.parameters
      ? Object.entries(codec.parameters)
          .map((_) => _.join("="))
          .join(";")
      : "";

    return this.isVideoCodecCompatible({ ...codec, sdpFmtpLine });
  }

  private isVideoCodecCompatible(codec: RTCRtpCodecCapability) {
    return (
      codec.clockRate === RtpCapabilityService.VIDEO_CLOCK_RATE &&
      codec.mimeType.toLocaleLowerCase() ===
        RtpCapabilityService.VIDEO_MIME_TYPE &&
      RtpCapabilityService.VIDEO_CAPABILITIES.every((_) =>
        codec.sdpFmtpLine?.includes(_)
      )
    );
  }

  private isAudioCodecCompatible(codec: RTCRtpCodecCapability) {
    return (
      codec.clockRate === RtpCapabilityService.AUDIO_CLOCK_RATE &&
      codec.mimeType.toLocaleLowerCase() ===
        RtpCapabilityService.AUDIO_MIME_TYPE &&
      codec.channels === RtpCapabilityService.AUDIO_CHANNELS
    );
  }
}
